import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "../i18n";
import { scrollToId, EASE } from "./Reveal";
import { ParticleCore } from "./ParticleCore";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const coreY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col overflow-hidden grain">
      {/* backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1100px 700px at 78% 30%, rgba(23,128,60,0.32), transparent 60%), radial-gradient(800px 600px at 10% 85%, rgba(168,214,127,0.10), transparent 60%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(168,214,127,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(168,214,127,0.4) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />

      <div className="relative mx-auto max-w-7xl w-full px-4 sm:px-6 pt-32 lg:pt-40 pb-10 grid lg:grid-cols-12 gap-10 items-center flex-1">
        {/* left — kinetic headline */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05, ease: EASE }}
            className="mono-label mb-6 flex items-center gap-3"
          >
            <span className="inline-block w-8 h-px bg-hadron-cyan" />
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-display uppercase font-bold leading-[0.94] tracking-tight text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-txt">
            <Line delay={0.15}>{t.hero.l1}</Line>
            <Line delay={0.3}><span className="text-hadron-cyan">{t.hero.l2}</span></Line>
            <Line delay={0.45}>{t.hero.l3}</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            className="mt-7 max-w-xl text-base sm:text-lg text-txt-2 leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("products")}
              className="group inline-flex items-center gap-3 bg-hadron-cyan clip-frame px-7 py-4 font-mono2 text-xs uppercase tracking-[0.2em] text-ink font-medium hover:bg-hadron-green transition-colors"
            >
              {t.hero.cta1}
              <ArrowDown size={14} className="transition-transform group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("contact")}
              className="inline-flex items-center gap-3 border border-line px-7 py-4 font-mono2 text-xs uppercase tracking-[0.2em] text-txt hover:border-hadron-cyan hover:text-hadron-cyan transition-colors"
            >
              {t.hero.cta2}
            </button>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-12 flex flex-wrap gap-2.5"
          >
            {[t.hero.chip1, t.hero.chip2, t.hero.chip3].map((c, i) => (
              <li
                key={i}
                data-testid={`hero-chip-${i + 1}`}
                className="border border-line bg-ink-2/60 backdrop-blur px-3.5 py-2 font-mono2 text-[10px] uppercase tracking-[0.16em] text-txt-2"
              >
                {c}
              </li>
            ))}
          </motion.ul>
        </div>

        {/* right — particle core + parallax product frame */}
        <motion.div style={{ y: coreY, opacity: fade }} className="lg:col-span-5 relative h-[380px] sm:h-[460px] lg:h-[560px]">
          <div className="absolute inset-0"><ParticleCore /></div>
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
            className="absolute bottom-2 right-0 w-44 sm:w-56 lg:w-64"
          >
            <div className="clip-frame border border-line p-2 bg-ink-2/70 backdrop-blur glow-cyan">
              <img
                src="/images/hero_outboard.jpg"
                alt={t.products.items[0].title}
                className="w-full h-40 sm:h-52 lg:h-56 object-cover"
                data-testid="hero-product-image"
              />
              <p className="mono-label !text-[9px] px-1 pt-2.5 pb-1">HD-X05 — {t.specs.m3.equiv}</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* bottom telemetry strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="relative border-t border-line"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex items-center justify-between font-mono2 text-[10px] uppercase tracking-[0.2em] text-txt-3">
          <span className="flex items-center gap-2" data-testid="hero-status">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-hadron-green opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-hadron-green" />
            </span>
            EU Hub — Online
          </span>
          <span className="hidden sm:block">Podolany 88 · Gdów · Małopolska</span>
          <span className="flex items-center gap-2 text-hadron-cyan">
            {t.hero.scroll}
            <motion.span animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}>
              <ArrowDown size={12} />
            </motion.span>
          </span>
        </div>
      </motion.div>
    </section>
  );
};
